import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Layout from "@/components/Layout";
import GlobeAdvanced from '@/components/GlobeAdvanced';
import { FaShieldAlt, FaSearch, FaExclamationTriangle, FaBookOpen, FaTools, FaChartLine, FaSearchDollar, FaNetworkWired, FaGlobe, FaImage, FaFileAlt, FaUsers, FaLock } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';

const EnhancedDashboard = () => {
  const { currentUser, userProfile, isEmailVerified } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Debug logs
    console.log("Current User:", currentUser);
    console.log("User Profile:", userProfile);
    // Redirect to verify email if not verified
    if (currentUser && !isEmailVerified) {
      navigate('/auth/verify-email');
    }
  }, [currentUser, userProfile, isEmailVerified, navigate]);

  // Get user's first name from profile, fallback to email if not available
  const displayName = userProfile?.firstName || currentUser?.email?.split('@')[0] || 'User';
  console.log("Display Name:", displayName);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <Layout>
      <motion.div 
        className="space-y-6 w-full px-2 sm:px-4 md:pl-0 max-w-full"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Welcome message */}
        <motion.div 
          className="bg-gradient-to-r from-truth/10 to-transparent backdrop-blur-sm border-l-4 border-truth p-4 rounded-lg shadow-lg mb-4"
          variants={itemVariants}
        >
          <h2 className="text-xl sm:text-2xl font-bold text-truth">{t('welcomeBack')}, {displayName}!</h2>
        </motion.div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <motion.h1 
            className="text-2xl sm:text-3xl font-bold text-truth"
            variants={itemVariants}
          >
            {t('dashboard')}
          </motion.h1>
        </div>

        {/* Globe visualization */}
        <motion.div variants={itemVariants}>
          <Card className="bg-black/20 backdrop-blur-sm border-gray-800 hover:border-truth/30 transition-all duration-300 overflow-hidden shadow-xl">
            <CardHeader className="bg-gradient-to-r from-truth/10 to-transparent">
              <CardTitle className="text-base sm:text-lg text-truth">{t('globalMisinfoTracker')}</CardTitle>
              <CardDescription className="text-xs sm:text-sm">{t('realTimeVisualization')}</CardDescription>
            </CardHeader>
            <CardContent className="h-64 sm:h-[400px] relative">
              <GlobeAdvanced />
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Stats overview */}
        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4"
          variants={itemVariants}
        >
          <StatCard title={t('analysesRun')} value="124" trend="+12%" />
          <StatCard title={t('misinformationDetected')} value="37" trend="-8%" />
          <StatCard title={t('sourcesVerified')} value="85" trend="+23%" />
          <StatCard title={t('reportsSubmitted')} value="41" trend="+15%" />
        </motion.div>

        {/* Main Features */}
        <motion.h2 
          className="text-xl sm:text-2xl font-bold mt-8 mb-4 text-truth"
          variants={itemVariants}
        >
          {t('mainFeatures')}
        </motion.h2>
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-x-auto"
          variants={itemVariants}
        >
          <ToolCard 
            icon={<FaChartLine className="h-6 w-6 text-truth" />}
            title={t('analyzeContent')}
            description={t('analyzeContentDesc')}
            linkTo="/detect"
          />
          <ToolCard 
            icon={<FaShieldAlt className="h-6 w-6 text-truth" />}
            title={t('sourceCredibility')}
            description={t('sourceCredibilityDesc')}
            linkTo="/sources"
          />
          <ToolCard 
            icon={<FaExclamationTriangle className="h-6 w-6 text-truth" />}
            title={t('reportMisinfo')}
            description={t('reportMisinfoDesc')}
            linkTo="/report"
          />
          <ToolCard 
            icon={<FaBookOpen className="h-6 w-6 text-truth" />}
            title={t('mediaLiteracy')}
            description={t('mediaLiteracyDesc')}
            linkTo="/learn"
          />
        </motion.div>

        {/* Secondary Features */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4"
          variants={itemVariants}
        >
          <ToolCard 
            icon={<FaSearchDollar className="h-6 w-6 text-truth" />}
            title="SOCMINT"
            description="Social media intelligence tools and advanced search capabilities"
            linkTo="/tools"
          />
          <ToolCard 
            icon={<FaNetworkWired className="h-6 w-6 text-truth" />}
            title="Network Analysis"
            description="Map relationships between sources, authors, and content"
            linkTo="/tools"
          />
          <ToolCard 
            icon={<FaUsers className="h-6 w-6 text-truth" />}
            title={t('community')}
            description="Collaborate with fact-checkers, researchers, and journalists"
            linkTo="/community"
          />
          <ToolCard 
            icon={<FaLock className="h-6 w-6 text-truth" />}
            title={t('privacySecurity')}
            description="Secure your investigations and protect your digital footprint"
            linkTo="/privacy-and-hacking"
          />
        </motion.div>

      </motion.div>
    </Layout>
  );
};

const StatCard = ({ title, value, trend }: { title: string, value: string, trend: string }) => {
  const isPositive = trend.startsWith('+');
  
  return (
    <Card className="bg-black/20 backdrop-blur-sm border-gray-800 hover:border-truth/30 transition-all duration-200 shadow-lg overflow-hidden">
      <CardContent className="p-6 relative">
        {/* Background glow effect */}
        <div className={`absolute top-0 right-0 h-16 w-16 rounded-full blur-xl opacity-20 ${isPositive ? 'bg-green-500' : 'bg-red-500'}`} />
        
        <p className="text-gray-400 text-sm font-medium">{title}</p>
        <div className="flex items-end justify-between mt-2">
          <p className="text-2xl sm:text-3xl font-bold text-white">{value}</p>
          <span className={`text-sm px-2 py-1 rounded-full ${isPositive ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'} flex items-center justify-center font-medium`}>
            {trend}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const ToolCard = ({ icon, title, description, linkTo }: { 
  icon: React.ReactNode, 
  title: string, 
  description: string,
  linkTo: string
}) => {
  const { t } = useLanguage();
  
  return (
    <Card className="bg-black/20 backdrop-blur-sm border-gray-800 hover:border-truth/50 transition-all duration-200 group shadow-lg hover:shadow-truth/10">
      <CardContent className="p-6">
        <div className="flex items-center space-x-4">
          <div className="rounded-full bg-gradient-to-br from-truth/20 to-truth/5 p-3 transform group-hover:scale-110 transition-all duration-200">
            {icon}
          </div>
          <div>
            <h3 className="font-bold text-white group-hover:text-truth transition-colors duration-200">{title}</h3>
            <p className="text-gray-400 text-sm">{description}</p>
          </div>
        </div>
        <Button asChild variant="ghost" className="w-full mt-4 hover:text-truth hover:bg-truth/10 transition-all duration-200 group-hover:translate-y-0 translate-y-1">
          <Link to={linkTo}>{t('launchTool')}</Link>
        </Button>
      </CardContent>
    </Card>
  );
};

const ActivityItem = ({ title, description, time, result }: {
  title: string,
  description: string,
  time: string,
  result: string
}) => {
  let resultColor = "bg-gray-700 text-gray-300";
  
  if (result === "Factual" || result === "True True" || result === "Factuel") {
    resultColor = "bg-green-900/30 text-green-400";
  } else if (result === "Manipulated" || result === "Low Credibility" || result === "Dem Don Edit Am" || result === "E No Reliable" || result === "Manipulé" || result === "Faible Crédibilité") {
    resultColor = "bg-red-900/30 text-red-400";
  } else if (result === "Misleading" || result === "E Dey Mislead" || result === "Trompeur") {
    resultColor = "bg-yellow-900/30 text-yellow-400";
  }
  
  return (
    <Card className="bg-black/20 backdrop-blur-sm border-gray-800 hover:border-truth/30 transition-all duration-200 shadow-lg overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 rounded-full bg-gradient-to-br from-truth/20 to-truth/5 p-2">
              <FaSearch className="h-4 w-4 text-truth" />
            </div>
            <div>
              <h4 className="font-medium text-white">{title}</h4>
              <p className="text-sm text-gray-400">{description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`text-xs px-2 py-1 rounded-full ${resultColor} font-medium`}>{result}</span>
            <span className="text-xs text-gray-500">{time}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EnhancedDashboard;
